import { makeObservable, observable, action } from 'mobx';
import Telemetry from '@core/Telemetry';
import Xlsx, { IDownload, dataField } from '@services/Xlsx';
import { RootStore } from './Root.store';
import LanguageStore from './Language.store';

type uploadedDevice = {
    [x: string]: string;
};

interface IUpload {
    header: string[];
    buffer: string | ArrayBuffer;
}

export default class DownloadStore {
    private xlsx: Xlsx;

    public filename = 'data';
    public devicesHeader = ['deviceName', 'imei', 'password', 'group'];
    public devicesDbHeader = ['imei', 'type', 'password', 'swVer', 'bootVer', 'hwVer'];

    @observable
    public loading: boolean;

    @observable
    public serverError: boolean;

    @observable
    public uploadError: boolean;

    @observable
    public uploadedDevices: uploadedDevice[] | null;

    constructor(private rootStore: RootStore) {
        this.xlsx = new Xlsx();
        this.setDefault();
        makeObservable(this);
    }

    @action
    setDefault() {
        this.loading = false;
        this.serverError = false;
        this.uploadError = false;
        this.uploadedDevices = null;
    }

    @action
    async downloadFullDeviceTelemetry() {
        const langStore = this.rootStore.lang;
        const getWord: LanguageStore['getWord'] =
            langStore.getWord.bind(langStore);
        const header = Telemetry.headers.map((title) => {
            const { units } = Telemetry;
            let headerName = getWord(title);
            if (title in units) {
                const unit = getWord(units[title as keyof typeof units]);
                headerName = `${headerName}, ${unit}`;
            }
            return headerName;
        });
        const timezone = this.rootStore.user.settings.timezone ?? 0;
        const telemetry = this.rootStore.telemetry.currentTelemetry;
        const data = telemetry.map((data) => {
            const result = new Telemetry(data, { timezone }).getArray();
            result[2] = getWord(result[2] ?? '');
            result[9] = getWord(result[9]);
            result[11] = getWord(result[11]);
            result[12] = getWord(result[12]);
            result[13] = getWord(result[13]);
            result[14] = getWord(result[14]);
            result[15] = getWord(result[15]);
            result[16] = getWord(result[16]);
            result[17] = getWord(result[17]);
            result[18] = getWord(result[18]);
            result[19] = getWord(result[19]);
            return result;
        });

        const filename = this.filename;
        const sheetname =
            this.rootStore.lang.lang === 'en' ? 'Sheet1' : 'Лиcт1';
        await this.xlsx.download({ header, data, filename, sheetname });
    }

    @action
    async downloadCustomData(
        header: string[],
        data: dataField[][],
        fn: string,
    ) {
        const filename = fn ?? this.filename;
        const sheetname =
            this.rootStore.lang.lang === 'en' ? 'Sheet1' : 'Лиcт1';
        await this.xlsx.download({ header, data, filename, sheetname });
    }

    @action
    async _downloadTemplate({
        filename,
        header,
    }: Pick<IDownload, 'filename' | 'header'>) {
        const sheetname =
            this.rootStore.lang.lang === 'en' ? 'Sheet1' : 'Лиcт1';
        await this.xlsx.downloadTemplate({ header, filename, sheetname });
    }

    @action
    async _upload({ buffer, header }: IUpload) {
        const data = await this.xlsx.parseData({ buffer });
        if (data) {
            const langStore = this.rootStore.lang;
            const getWord: LanguageStore['getWord'] =
                langStore.getWord.bind(langStore);
            const result = data.map((val: object) =>
                header
                    .filter(
                        (key) =>
                            typeof val === 'object' &&
                            val[getWord(key) as keyof typeof val],
                    )
                    .map((key) => ({
                        [key]: val[getWord(key) as keyof typeof val],
                    }))
                    .reduce((acc, val) => ({ ...acc, ...val }), {}),
            );
            if (result.length > 0) {
                this.uploadError = false;
                this.uploadedDevices = result;
            } else {
                this.uploadError = true;
            }
        } else this.uploadError = true;
    }

    @action
    downloadDevicesTemplate() {
        const langStore = this.rootStore.lang;
        const getWord: LanguageStore['getWord'] =
            langStore.getWord.bind(langStore);
        const header = this.devicesHeader.map(getWord);
        const filename = getWord('devices');
        this._downloadTemplate({ filename, header });
    }

    @action
    downloadDbDevicesTemplate() {
        const langStore = this.rootStore.lang;
        const getWord: LanguageStore['getWord'] =
            langStore.getWord.bind(langStore);
        const header = this.devicesDbHeader.map(getWord);
        const filename = getWord('devices');
        this._downloadTemplate({ filename, header });
    }

    @action
    uploadDevices(buffer: string | ArrayBuffer) {
        const header = this.devicesHeader;
        this._upload({ buffer, header });
    }

    @action
    uploadDbDevices(buffer: string | ArrayBuffer) {
        const header = this.devicesDbHeader;
        this._upload({ buffer, header });
    }

    @action
    resetUploadError() {
        this.uploadError = false;
    }

    @action
    resetUploadedDevices() {
        this.uploadedDevices = null;
    }
}
